import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { AppShell3 } from '@/components/app-shell-3';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PageFadeIn } from '@/components/PageFadeIn';
import { PageTransition } from '@/components/PageTransition';
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  User,
  Calendar,
  CheckSquare,
  MessageSquare,
  Send,
  Check,
  Users,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { format } from 'date-fns';

export default function ChecklistDetailPage() {
  const { checklistId } = useParams();
  const navigate = useNavigate();
  const { userProfile, company } = useAuth();
  usePageTitle('Checklist');

  const [checklist, setChecklist] = useState(null);
  const [items, setItems] = useState([]);
  const [companyMembers, setCompanyMembers] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openPopoverId, setOpenPopoverId] = useState(null);

  // Fetch checklist details
  const fetchChecklist = async () => {
    if (!company?.id) return;

    setLoading(true);
    try {
      // SECURITY: Fetch checklist and verify it belongs to the current company
      const { data, error } = await supabase
        .from('checklists')
        .select(`
          *,
          template:template_id(id, name, description),
          creator:created_by(id, first_name, last_name, display_name, avatar_url)
        `)
        .eq('id', checklistId)
        .eq('company_id', company.id)  // SECURITY: Only fetch if belongs to user's company
        .single();

      if (error) {
        // Could be not found OR doesn't belong to this company
        console.error('Security: Checklist not found or access denied');
        toast.error('Checklist not found or you do not have permission to view it');
        navigate('/checklists');
        return;
      }

      setChecklist(data);
    } catch (error) {
      console.error('Error fetching checklist:', error);
      toast.error('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  // Fetch checklist items with assignments
  const fetchItems = async () => {
    try {
      // Fetch items
      const { data: itemsData, error: itemsError } = await supabase
        .from('checklist_items')
        .select(`
          *,
          completed_user:completed_by(id, first_name, last_name, display_name, avatar_url)
        `)
        .eq('checklist_id', checklistId)
        .order('order_index');

      if (itemsError) throw itemsError;

      // Fetch assignments for all items
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('checklist_item_assignments')
        .select(`
          checklist_item_id,
          user:user_id(id, first_name, last_name, display_name, avatar_url)
        `)
        .in('checklist_item_id', (itemsData || []).map(item => item.id));

      if (assignmentsError) throw assignmentsError;

      // Group assignments by item
      const assignmentsByItem = {};
      (assignmentsData || []).forEach(assignment => {
        if (!assignmentsByItem[assignment.checklist_item_id]) {
          assignmentsByItem[assignment.checklist_item_id] = [];
        }
        assignmentsByItem[assignment.checklist_item_id].push(assignment.user);
      });

      // Add assignments to items
      const itemsWithAssignments = (itemsData || []).map(item => ({
        ...item,
        assigned_users: assignmentsByItem[item.id] || [],
      }));

      setItems(itemsWithAssignments);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to load checklist items');
    }
  };

  // Fetch company members for assignment
  const fetchCompanyMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('company_members')
        .select(`
          user:user_id(id, first_name, last_name, display_name, avatar_url)
        `)
        .eq('company_id', company.id);

      if (error) throw error;

      const members = (data || [])
        .map(m => m.user)
        .filter(u => u !== null);

      setCompanyMembers(members);
    } catch (error) {
      console.error('Error fetching company members:', error);
    }
  };

  // Fetch comments
  const fetchComments = async () => {
    try {
      const { data, error } = await supabase
        .from('checklist_comments')
        .select(`
          *,
          user_profiles (
            id,
            display_name,
            first_name,
            last_name,
            avatar_url
          )
        `)
        .eq('checklist_id', checklistId)
        .order('created_date', { ascending: false });

      if (error) throw error;
      setComments(data || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  // Add a new comment
  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      setSubmittingComment(true);
      const { error } = await supabase
        .from('checklist_comments')
        .insert({
          checklist_id: checklistId,
          created_by: userProfile.id,
          content: newComment.trim()
        });

      if (error) throw error;

      setNewComment('');
      await fetchComments();
      toast.success('Comment added');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  useEffect(() => {
    if (checklistId && company?.id) {
      fetchChecklist();
      fetchItems();
      fetchCompanyMembers();
      fetchComments();
    }
  }, [checklistId, company?.id]);

  // Toggle item completion
  const toggleItemCompletion = async (item) => {
    const newCompletionStatus = !item.is_completed;

    // Optimistic update
    setItems(items.map(i =>
      i.id === item.id
        ? {
            ...i,
            is_completed: newCompletionStatus,
            completed_date: newCompletionStatus ? new Date().toISOString() : null,
            completed_by: newCompletionStatus ? userProfile.id : null,
          }
        : i
    ));

    try {
      const updates = {
        is_completed: newCompletionStatus,
        completed_date: newCompletionStatus ? new Date().toISOString() : null,
        completed_by: newCompletionStatus ? userProfile.id : null,
      };

      const { error } = await supabase
        .from('checklist_items')
        .update(updates)
        .eq('id', item.id);

      if (error) throw error;

      // Refresh items to get updated completion data
      await fetchItems();

      toast.success(newCompletionStatus ? 'Item completed!' : 'Item reopened');
    } catch (error) {
      console.error('Error toggling item:', error);
      toast.error('Failed to update item');
      // Revert optimistic update
      await fetchItems();
    }
  };

  // Toggle user assignment
  const toggleUserAssignment = async (itemId, userId, isCurrentlyAssigned) => {
    try {
      if (isCurrentlyAssigned) {
        // Remove assignment
        const { error } = await supabase
          .from('checklist_item_assignments')
          .delete()
          .eq('checklist_item_id', itemId)
          .eq('user_id', userId);

        if (error) throw error;
        toast.success('Assignment removed');
      } else {
        // Add assignment
        const { error } = await supabase
          .from('checklist_item_assignments')
          .insert({
            checklist_item_id: itemId,
            user_id: userId,
          });

        if (error) throw error;
        toast.success('User assigned');
      }

      await fetchItems();
    } catch (error) {
      console.error('Error toggling assignment:', error);
      toast.error('Failed to update assignment');
    }
  };

  if (loading) {
    return (
      <AppShell3 pageTitle="" pageDescription="">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  if (!checklist) {
    return (
      <AppShell3 pageTitle="" pageDescription="">
        <div className="text-center py-16">
          <h3 className="text-xl font-semibold mb-2">Checklist not found</h3>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </AppShell3>
    );
  }

  const totalItems = items.length;
  const completedItems = items.filter(i => i.is_completed).length;
  const progress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <PageFadeIn>
      <AppShell3 pageTitle="" pageDescription="">
        <PageTransition>
          <div className="container mx-auto px-6 py-6 max-w-4xl">
            {/* Header */}
            <div className="mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>

              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold mb-2">{checklist.name}</h1>
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="secondary">
                      {checklist.template?.name}
                    </Badge>
                    {checklist.is_completed ? (
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100 gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Complete
                      </Badge>
                    ) : (
                      <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 gap-1">
                        <Clock className="h-3 w-3" />
                        In Progress
                      </Badge>
                    )}
                  </div>
                  {checklist.template?.description && (
                    <p className="text-sm text-muted-foreground mb-3">
                      {checklist.template.description}
                    </p>
                  )}

                  {/* Checklist Meta Info */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-5 w-5">
                        <AvatarImage src={checklist.creator?.avatar_url} />
                        <AvatarFallback>
                          <User className="h-3 w-3" />
                        </AvatarFallback>
                      </Avatar>
                      <span>Created by {checklist.creator?.display_name || `${checklist.creator?.first_name} ${checklist.creator?.last_name}`}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      <span>{new Date(checklist.created_date).toLocaleDateString()}</span>
                    </div>
                    {checklist.completed_date && (
                      <div className="flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        <span>Completed {new Date(checklist.completed_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Checklist Items */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <CheckSquare className="h-5 w-5" />
                    Checklist Items
                  </CardTitle>

                  {/* Compact Progress */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 w-[200px]">
                      <Progress value={progress} className="flex-1 h-2" />
                      <span className="text-xs text-muted-foreground whitespace-nowrap min-w-[50px]">
                        {completedItems}/{totalItems}
                      </span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {items.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No items in this checklist
                  </div>
                ) : (
                  <div className="space-y-1">
                    {items.map((item, index) => (
                      <div
                        key={item.id}
                        className={`py-3 px-2 flex items-start gap-3 hover:bg-muted/30 rounded transition-colors ${
                          item.is_completed ? 'opacity-60' : ''
                        }`}
                      >
                        {/* Checkbox */}
                        <Checkbox
                          checked={item.is_completed}
                          onCheckedChange={() => toggleItemCompletion(item)}
                          className="mt-0.5"
                        />

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium ${item.is_completed ? 'line-through text-muted-foreground' : ''}`}>
                            {index + 1}. {item.title}
                          </div>
                          {item.description && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {item.description}
                            </div>
                          )}

                          {/* Meta info */}
                          {item.is_completed && item.completed_date && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                              <CheckCircle2 className="h-3 w-3" />
                              <span>
                                Completed {new Date(item.completed_date).toLocaleDateString()}
                              </span>
                              {item.completed_user && (
                                <span>by {item.completed_user.display_name || `${item.completed_user.first_name} ${item.completed_user.last_name}`}</span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Assignment - Multi-Select */}
                        <div className="flex-shrink-0">
                          <Popover
                            open={openPopoverId === item.id}
                            onOpenChange={(open) => setOpenPopoverId(open ? item.id : null)}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs justify-between w-[180px]"
                              >
                                {item.assigned_users && item.assigned_users.length > 0 ? (
                                  <div className="flex items-center gap-1.5 overflow-hidden">
                                    <Users className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">
                                      {item.assigned_users.length === 1
                                        ? item.assigned_users[0].display_name ||
                                          `${item.assigned_users[0].first_name} ${item.assigned_users[0].last_name}`
                                        : `${item.assigned_users.length} assigned`}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">Unassigned</span>
                                )}
                                <ChevronDown className="h-3.5 w-3.5 opacity-50 flex-shrink-0" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[240px] p-0" align="end">
                              <Command>
                                <CommandInput placeholder="Search members..." className="h-8" />
                                <CommandList>
                                  <CommandEmpty>No member found.</CommandEmpty>
                                  <CommandGroup>
                                    {companyMembers.map((member) => {
                                      const isAssigned = item.assigned_users?.some(u => u.id === member.id);
                                      return (
                                        <CommandItem
                                          key={member.id}
                                          value={member.display_name || `${member.first_name} ${member.last_name}`}
                                          onSelect={() => toggleUserAssignment(item.id, member.id, isAssigned)}
                                          className="cursor-pointer"
                                        >
                                          <div className="flex items-center gap-2 flex-1">
                                            <div className={`h-4 w-4 border rounded-sm flex items-center justify-center ${
                                              isAssigned ? 'bg-primary border-primary' : 'border-input'
                                            }`}>
                                              {isAssigned && <Check className="h-3 w-3 text-primary-foreground" />}
                                            </div>
                                            <Avatar className="h-5 w-5">
                                              <AvatarImage src={member.avatar_url} />
                                              <AvatarFallback>
                                                <User className="h-3 w-3" />
                                              </AvatarFallback>
                                            </Avatar>
                                            <span className="text-sm">
                                              {member.display_name || `${member.first_name} ${member.last_name}`}
                                            </span>
                                          </div>
                                        </CommandItem>
                                      );
                                    })}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Comments Section */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Comments
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Add Comment Form */}
                <div className="mb-6">
                  <Textarea
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="min-h-[80px] mb-2"
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || submittingComment}
                      size="sm"
                    >
                      <Send className="h-3.5 w-3.5 mr-2" />
                      {submittingComment ? 'Posting...' : 'Post Comment'}
                    </Button>
                  </div>
                </div>

                {/* Comments List */}
                {comments.length > 0 ? (
                  <div className="space-y-4">
                    <Separator />
                    <div className="space-y-4">
                      {comments.map((comment) => (
                        <div key={comment.id} className="flex gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={comment.user_profiles?.avatar_url} />
                            <AvatarFallback>
                              <User className="h-4 w-4" />
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 mb-1">
                              <span className="font-medium text-sm">
                                {comment.user_profiles?.display_name ||
                                  `${comment.user_profiles?.first_name} ${comment.user_profiles?.last_name}`}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(comment.created_date), 'MMM d, yyyy \'at\' h:mm a')}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                              {comment.content}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No comments yet. Be the first to add one!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </PageTransition>
      </AppShell3>
    </PageFadeIn>
  );
}
